import { staleTimes, useQuery } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { Category } from '../categories.ts';
import type { ProductFormValues } from '../product-form.ts';
import type { TenantDetail } from '../product.ts';
import { queryKeys, usePageContext } from '../../usePageContext.ts';
import { referencePrefixOf } from './reference-prefix.ts';

type SuggestionInput = {
  readonly api: TypedClient;
  readonly subsidiaries: TenantDetail['subsidiaries'];
  readonly categories: readonly Category[];
};

const slugOfCategory = (category: Category | undefined, locale: string): string | null =>
  (category?.translations.find((entry) => entry.locale === locale) ?? category?.translations[0])?.slug ??
  null;

export const useReferenceSuggestion = ({ api, subsidiaries, categories }: SuggestionInput): void => {
  const { tenantId } = usePageContext();
  const { control, getValues, setValue } = useFormContext<ProductFormValues>();
  const [subsidiaryId, categoryId, defaultLocale] = useWatch<
    ProductFormValues,
    ['subsidiaryId', 'categoryId', 'defaultLocale']
  >({ control, name: ['subsidiaryId', 'categoryId', 'defaultLocale'] });
  const lastSuggestion = useRef('');
  const prefix = referencePrefixOf(
    subsidiaries.find((subsidiary) => subsidiary.id === subsidiaryId)?.slug ?? null,
    slugOfCategory(
      categories.find((category) => category.id === categoryId),
      defaultLocale,
    ),
  );
  const suggestion = useQuery({
    queryKey: queryKeys.nextReference(tenantId, prefix),
    queryFn: () => api.get('/admin/products/references/next', { query: { prefix } }),
    enabled: prefix !== '',
    staleTime: staleTimes.catalog,
    select: (data) => data.reference,
  }).data;

  useEffect(() => {
    if (suggestion === undefined) return;
    const typed = getValues('reference').trim();
    if (typed !== '' && typed !== lastSuggestion.current) return;
    lastSuggestion.current = suggestion;
    setValue('reference', suggestion, { shouldDirty: true, shouldValidate: true });
  }, [suggestion, getValues, setValue]);
};
